// Client for dubu-api's public /pro market-data surface.
//
// All market data is served from OUR ingested tape, never from Daya directly: Daya's public
// endpoints are capped at 100 req/min per IP, so one ingester fans out to any number of
// browsers. Client traffic must never touch api.pro.daya.co.

export const API_BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:3000/api/v1";

/**
 * The only market Daya actually serves. The docs advertise USDT-NGN / USDC-NGN too, but
 * verified 2026-07-16: /markets returns USD-NGN alone, and the USDT-NGN alias 404s on
 * /market-trades and 500s on /orderbook. Base asset is USD; USDT/USDC are funding rails
 * into the same unified USD balance, not separate books.
 */
export const DEFAULT_SYMBOL = "USD-NGN";

export interface DayaEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface Candle {
  time: number; // unix seconds at bucket open
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
}

export interface CandlesResponse {
  symbol: string;
  interval: string;
  candles: Candle[];
  /** Our tape's earliest trade, the chart cannot honestly show anything before this. */
  history_starts_at: string | null;
}

export interface OrderbookLevel {
  price: string;
  quantity: string;
}

export interface Orderbook {
  symbol: string;
  base_asset: string;
  quote_asset: string;
  bids: OrderbookLevel[];
  asks: OrderbookLevel[];
  stats: {
    best_bid: string;
    best_ask: string;
    spread: string;
    spread_percent: string;
    mid_price: string;
  };
  depth: number;
  timestamp: string;
}

export interface Trade {
  id: string;
  price: string;
  quantity: string;
  side: "buy" | "sell";
  traded_at: string;
}

export interface TradesResponse {
  symbol: string;
  trades: Trade[];
}

export type Interval = "1m" | "5m" | "15m" | "1h" | "4h" | "1d";

/**
 * Default to 1h, not 1m. Measured 2026-07-16: USD-NGN trades ~4.7x/hour, so only ~6% of
 * 1-minute buckets contain a trade: a 1m chart is mostly gaps and reads as broken.
 */
export const DEFAULT_INTERVAL: Interval = "1h";

const request = async <T>(path: string, signal?: AbortSignal): Promise<T> => {
  const res = await fetch(`${API_BASE}${path}`, {
    signal,
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`Request failed (${res.status}): ${path}`);
  }
  const body = (await res.json()) as DayaEnvelope<T>;
  if (!body.success) throw new Error(body.message || "Request failed");
  return body.data;
};

export const fetchCandles = (
  symbol: string,
  interval: Interval,
  signal?: AbortSignal
): Promise<CandlesResponse> =>
  request<CandlesResponse>(
    `/pro/markets/${encodeURIComponent(symbol)}/candles?interval=${interval}`,
    signal
  );

export const fetchOrderbook = (
  symbol: string,
  depth = 12,
  signal?: AbortSignal
): Promise<Orderbook> =>
  request<Orderbook>(
    `/pro/markets/${encodeURIComponent(symbol)}/orderbook?depth=${depth}`,
    signal
  );

export const fetchTrades = (
  symbol: string,
  limit = 40,
  signal?: AbortSignal
): Promise<TradesResponse> =>
  request<TradesResponse>(
    `/pro/markets/${encodeURIComponent(symbol)}/trades?limit=${limit}`,
    signal
  );
